import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import useVehicle from "../../../system/useVehicle";
import { useAsync } from "react-use";
import axios from "axios";
import { VehicleAssessment, VehicleAssessmentAttachment } from "../../../system/Domain";
import Layout from "../../../Components/Layout/Layout";
import LotScanContextMenu from "../LotScanContextMenu";
import VehicleHeaderMenu from "../VehicleHeaderMenu";
import VehicleSubMenu from "../VehicleSubMenu";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import VehicleHeader from "../VehicleHeader";
import Tile from "../../../Components/Tiles/Tile";
import TileContent from "../../../Components/Tiles/TileContent";
import AssessmentAttachmentList from "./AssessmentAttachmentList";
import parseContentDispositionFilename from "../../../system/parseContentDispositionFilename";
import fileDownload from "js-file-download";
import AssessmentDamageList from "./AssessmentDamageList";
import AssessmentOverviewTile from "./AssessmentOverviewTile";
import rejectVehicleAssessment from "./rejectVehicleAssessment";
import _ from "lodash";
import VehicleNavigation from "Navigation/VehicleNavigation";
import AssessmentPageSidebar from "./AssessmentPageSidebar";

const AssessmentPage = () => {
	const { id } = useParams<{ id: string }>();
	const { assessmentId } = useParams<{ assessmentId: string }>();
	const [vehicle] = useVehicle(id);
	const [assessment, setAssessment] = useState<VehicleAssessment | null>(null);

	const history = useHistory();

	const { value: assessments } = useAsync(async () => {
		if (!vehicle) return [];

		const { data: results } = await axios.get<VehicleAssessment[]>(`/api/assessments?vin=${vehicle.vin}`);
		return results;
	}, [vehicle]);

	useEffect(() => {
		if (!assessments || assessments.length === 0) {
			setAssessment(null);
			return;
		}

		if (assessmentId === "latest") {
			setAssessment(_.last(assessments.filter((a) => !a.dateRejected)) || null);
		} else {
			setAssessment(assessments.find((a) => a.id === assessmentId) || null);
		}
	}, [assessments, vehicle, assessmentId]);

	const handleDownload = async (attachment: VehicleAssessmentAttachment) => {
		const response = await axios.get(`/api/assessments/${assessment!.id}/attachments/${attachment.storageFile.hash}`, {
			responseType: "blob"
		});

		const filename = parseContentDispositionFilename(response.headers["content-disposition"]);
		fileDownload(response.data, filename);
	};

	const canAddToRemarketing = vehicle
		&& assessment
		&& !vehicle.remarketing.saleId
		&& !vehicle.remarketing.assessment?.assessmentId
		&& (assessment.source === "Lea" || assessment.source === "Macadam");

	const handleAddToRemarketing = async () => {
		if (!canAddToRemarketing) return;

		await axios.post(`/api/vehicles/${vehicle.id}/remarketing/assessment`, {
			assessmentId: assessment.id
		});

		history.push(`/vehicles/${vehicle.id}/remarketing`);
	};

	const canWithdrawRejection = false;
	const canReject = vehicle && assessment && (assessment.source === "Lea" || assessment.source === "Macadam");

	const handleRejectAssessment = async () => {
		if (!canReject) return;

		const result = await rejectVehicleAssessment({});

		const formData = new FormData();
		formData.append("reason", result.reason);
		formData.append("topic", result.topic);
		if (result.file) {
			formData.append("file", result.file);
		}

		const { data: newVehicleAssessment } = await axios.post<VehicleAssessment>(`/api/assessments/${assessment.id}/reject`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		});

		setAssessment(newVehicleAssessment);
	};

	const handleWithdrawAssessmentRejection = async () => {
		if (!canWithdrawRejection) return;

		const { data: newVehicleAssessment } = await axios.post<VehicleAssessment>(`/api/assessments/${assessment.id}/withdraw-rejection`);

		setAssessment(newVehicleAssessment);
	};

	return (
		<Layout
			title="Gutachten"
			plateNumber={vehicle?.plateNumber}
			navigation={<VehicleNavigation vehicle={vehicle} />}
			contextMenu={[<LotScanContextMenu />, <VehicleHeaderMenu vehicle={vehicle} />]}
			subMenu={vehicle && <VehicleSubMenu vehicle={vehicle} />}
			sidebar={
				<AssessmentPageSidebar
					vehicle={vehicle}
					canAddToRemarketing={canAddToRemarketing}
					handleAddToRemarketing={handleAddToRemarketing}
					canWithdrawRejection={canWithdrawRejection}
					handleWithdrawRejection={handleWithdrawAssessmentRejection}
					canReject={canReject}
					handleRejection={handleRejectAssessment}
					vehicleAssessment={assessment}
					setVehicleAssessment={setAssessment}
				/>
			}
		>
			{!vehicle && <CircularProgress size={48} />}
			{vehicle && (
				<>
					<Grid container spacing={2} alignItems="flex-end" justifyContent="space-between">
						<Grid item>
							<VehicleHeader vehicle={vehicle} />
						</Grid>
					</Grid>
					{!assessment && (
						<Box mt={4}>
							<Typography>Dem Fahrzeug ist kein Gutachten zugeordnet</Typography>
						</Box>
					)}
					{assessment && (
						<Box mt={4}>
							<Grid container spacing={6}>
								<Grid item xs={12} md={6} lg={4}>
									<AssessmentOverviewTile assessment={assessment} />
								</Grid>

								<Grid item xs={12} md={6} lg={4}>
									<Tile title="Reparaturen">
										<TileContent dense>
											<AssessmentDamageList assessment={assessment} />
										</TileContent>
									</Tile>
								</Grid>
								<Grid item xs={12} md={6} lg={4}>
									<Tile title="Anlagen">
										<TileContent>
											<AssessmentAttachmentList onSelect={handleDownload} attachments={assessment.attachments} />
										</TileContent>
									</Tile>
								</Grid>
							</Grid>
						</Box>
					)}
				</>
			)}
		</Layout>
	);
};

export default AssessmentPage;
